import { Component, OnInit, ViewChild } from "@angular/core";
import { ApiCallingService } from "../../services/api-calling.service";
import { ConfigApi } from "../../config-api";
import { Globals } from "../../common/global";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { Router, ActivatedRoute } from "@angular/router";
import { NgForm, FormGroup, FormBuilder } from "@angular/forms";
import { HttpParams } from "@angular/common/http";
import { EMPLOYEE_ROUTES } from "../../shared/sidebar/employee-menu-item";

interface Permission {
  category: string;
  title: string;
  module: string;
}

@Component({
  selector: "app-employee",
  templateUrl: "./employee.component.html",
  styleUrls: ["./employee.component.css"],
})
export class EmployeeComponent implements OnInit {
  @ViewChild("specialityform", { static: true }) specialityform: NgForm =
    Object.create(null);

  modeltitle: any;
  page = 1;
  pageSize = 10;
  data = [];
  totalcount = 0;
  detail: any;
  packages = [];
  pattern = /(\w+)$/;
  pId: any;
  showPassword = false;
  editUser: FormGroup | undefined;
  keepOrder = (a: any, b: any): number => 0;

  // For employee details and password change
  employeeDetails: any = {};
  passwordData = {
    newPassword: "",
    confirmPassword: "",
  };
  showNewPassword = false;
  showConfirmPassword = false;

  permissionModules: Permission[] = [];

  extractPermissionsFromRoutes(): Permission[] {
    const permissions: Permission[] = [];

    EMPLOYEE_ROUTES.forEach((route: any) => {
      if (route.submenu?.length) {
        route.submenu.forEach((item: any) => {
          permissions.push({
            category: route.title,
            title: item.title,
            module: this.pathToModule(item.path),
          });
        });
      } else {
        permissions.push({
          category: route.title,
          title: route.title,
          module: this.pathToModule(route.path),
        });
      }
    });

    return permissions;
  }

  pathToModule(path: string): string {
    const segments = path.split("/").filter(Boolean);
    return segments[segments.length - 1] || "unknown";
  }

  // Grouped permissions by category
  permissionsByCategory: { [key: string]: Permission[] } = {};
  orderedCategories: any;

  selectedPermissions: any = [];

  constructor(
    private modalService: NgbModal,
    private apiCallingService: ApiCallingService,
    private toastr: ToastrService,
    public Globals: Globals,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.permissionModules = this.extractPermissionsFromRoutes();
    this.getList();
    this.groupPermissionsByCategory();
    this.editUser = this.fb.group({});
  }

  groupPermissionsByCategory(): void {
    const sidebarOrder: string[] = [
      "Dashboard",
      "Users/Customers",
      "Employee",
      "Vending",
      "Packages",
      "Assets",
      "Payment",
      "Waiver forms",
      "History",
      "Notifications",
      "Platform Support",
    ];

    const grouped: Record<string, any[]> = {};

    // Step 1: Group permissions by category
    this.permissionModules.forEach((module) => {
      if (!grouped[module.category]) {
        grouped[module.category] = [];
      }
      grouped[module.category].push(module);
    });

    // Step 2: Rebuild permissionsByCategory in the correct sidebar order
    this.permissionsByCategory = {};

    for (const category of sidebarOrder) {
      if (grouped[category]) {
        this.permissionsByCategory[category] = grouped[category];
      }
    }
  }

  openModal(targetModal: NgbModal, data: any, title: any) {
    this.modeltitle = title;
    this.detail = {
      name: "",
      email: "",
      username: "",
      password: "",
      permissions: [],
    };
    this.modalService.open(targetModal, {
      centered: true,
      backdrop: "static",
      size: title === "Add Employee" ? "lg" : "md",
    });
    this.detail = Object.assign({}, data);

    // Reset selected permissions
    this.selectedPermissions = this.detail.permissions || [];

    // Add dashboard permission by default for new employees
    if (title === "Add Employee") {
      this.addDefaultDashboardPermission();
    }
  }

  // Add dashboard permission by default
  addDefaultDashboardPermission(): void {
    // Find the dashboard permission

    const dashboardPermission = this.permissionModules.find(
      (p) => p.category == "Dashboard"
    );

    if (
      dashboardPermission &&
      !this.isPermissionSelected(dashboardPermission.module)
    ) {
      this.selectedPermissions.push({ ...dashboardPermission });
    }
  }

  openEmployeeDetailsModal(targetModal: NgbModal, employee: any) {
    this.employeeDetails = employee;

    // Convert string module permissions to full permission objects if needed
    if (employee.permissions && Array.isArray(employee.permissions)) {
      if (typeof employee.permissions[0] === "string") {
        // Convert from string array to Permission objects
        this.selectedPermissions = this.convertToPermissionObjects(
          employee.permissions
        );
      } else {
        // Already in correct format
        this.selectedPermissions = employee.permissions;
      }
    } else {
      this.selectedPermissions = [];
      // Add dashboard permission by default if viewing permissions
      this.addDefaultDashboardPermission();
    }

    this.passwordData = {
      newPassword: "",
      confirmPassword: "",
    };

    this.modalService.open(targetModal, {
      centered: true,
      backdrop: "static",
      size: "lg",
    });
  }

  // Toggle all permissions across all categories
  toggleAllPermissions(event: any): void {
    const isChecked = event.target.checked;

    if (isChecked) {
      // Select all permissions
      this.selectedPermissions = [...this.permissionModules];
    } else {
      // Deselect all permissions except dashboard
      this.selectedPermissions = [];
      // Re-add dashboard permission
      this.addDefaultDashboardPermission();
    }
  }

  // Toggle all permissions within a specific category
  toggleCategoryPermissions(category: string, event: any): void {
    const isChecked = event.target.checked;
    const categoryPermissions = this.permissionsByCategory[category];

    if (isChecked) {
      // Add all permissions from this category that aren't already selected
      categoryPermissions.forEach((permission) => {
        if (!this.isPermissionSelected(permission.module)) {
          this.selectedPermissions.push({ ...permission });
        }
      });
    } else {
      // If it's the Dashboard category, keep at least one dashboard permission
      if (category == "Dashboard") {
        const dashboardPermission = categoryPermissions.find(
          (p) => p.module == "dashboard"
        );

        // Remove all permissions from this category except the main dashboard one
        this.selectedPermissions = this.selectedPermissions.filter(
          (p: any) =>
            !categoryPermissions.some((cp) => cp.module === p.module) ||
            (dashboardPermission && p.module === dashboardPermission.module)
        );
      } else {
        // Remove all permissions from this category
        this.selectedPermissions = this.selectedPermissions.filter(
          (p: any) => !categoryPermissions.some((cp) => cp.module === p.module)
        );
      }
    }
  }

  // Check if all permissions are selected
  areAllPermissionsSelected(): boolean {
    return this.selectedPermissions.length === this.permissionModules.length;
  }

  // Check if all permissions in a category are selected
  areCategoryPermissionsSelected(category: string): boolean {
    const categoryPermissions = this.permissionsByCategory[category];
    return categoryPermissions.every((permission) =>
      this.isPermissionSelected(permission.module)
    );
  }

  // Convert module strings to Permission objects
  convertToPermissionObjects(moduleNames: string[]): Permission[] {
    const result: Permission[] = [];

    moduleNames.forEach((moduleName) => {
      const foundPermission = this.permissionModules.find(
        (p) => p.module === moduleName
      );
      if (foundPermission) {
        result.push({ ...foundPermission });
      }
    });

    // Ensure dashboard permission is included
    const dashboardPermission = this.permissionModules.find(
      (p) => p.category == "Dashboard"
    );

    if (
      dashboardPermission &&
      !result.some((p) => p.module === dashboardPermission.module)
    ) {
      result.push({ ...dashboardPermission });
    }

    return result;
  }

  // Toggle permission selection
  togglePermission(module: string): void {
    const foundPermission = this.permissionModules.find(
      (p) => p.module === module
    );
    if (!foundPermission) return;

    // If it's the dashboard module, don't allow deselection
    if (foundPermission.category == "Dashboard") {
      if (!this.isPermissionSelected(module)) {
        this.selectedPermissions.push({ ...foundPermission });
      }
      return;
    }

    const index = this.selectedPermissions.findIndex(
      (p: any) => p.module === module
    );
    if (index === -1) {
      this.selectedPermissions.push({ ...foundPermission });
    } else {
      this.selectedPermissions.splice(index, 1);
    }
  }

  // Check if a permission is selected
  isPermissionSelected(module: string): boolean {
    return this.selectedPermissions.some((p: any) => p.module === module);
  }

  // Check if a permission is dashboard and should be disabled
  isDashboardPermission(module: string): boolean {
    const permission = this.permissionModules.find((p) => p.module === module);
    return permission?.category == "Dashboard";
  }

  savePermissions(): void {
    const employeeId = this.employeeDetails?._id;

    const cleanedPermissions: any = this.selectedPermissions.map(
      ({ _id, ...rest }: any) => rest
    );

    // Create payload with cleaned permissions
    const payload = {
      permissions: cleanedPermissions,
    };

    // Make the API call using the PATCH method to the specified endpoint
    this.apiCallingService
      .callAPI(
        payload,
        "PUT",
        ConfigApi.URLS.EMPLOYEE.UPDATE + "/" + employeeId
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success(res.msg || "Permissions updated successfully");
            this.getList();
            this.closeBtnClick();
            this.ngOnInit();
          } else {
            this.toastr.error(res.msg || "Failed to update permissions");
          }
        },
        (err) => {
          this.toastr.error(
            err.error?.msg || "Error updating permissions",
            "Error"
          );
        }
      );
  }

  // Toggle password visibility for different password fields
  togglePasswordVisibility(field?: string): void {
    if (!field) {
      this.showPassword = !this.showPassword;
      return;
    }
    if (field === "new") {
      this.showNewPassword = !this.showNewPassword;
    } else if (field === "confirm") {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  // Handle password change submission
  onChangePasswordSubmit(form: NgForm) {
    if (!form.valid) {
      return;
    }

    if (this.passwordData.newPassword !== this.passwordData.confirmPassword) {
      this.toastr.error("New password and confirm password do not match");
      return;
    }

    const body: any = new HttpParams()
      .set("employee_id", this.employeeDetails._id)
      .set("new_password", this.passwordData.newPassword);

    this.apiCallingService
      .callAPI(body, "POST", ConfigApi.URLS.EMPLOYEE.CHANGE_PASSWORD)
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success(res.msg || "Password changed successfully");
            // Reset form after successful password change
            this.passwordData = {
              newPassword: "",
              confirmPassword: "",
            };
            form.resetForm();
            this.closeBtnClick();
          } else {
            this.toastr.error(res.msg || "Failed to change password");
          }
        },
        (err) => {
          this.toastr.error(
            err.error.msg || "Error changing password",
            "Error"
          );
        }
      );
  }

  closeBtnClick() {
    this.modalService.dismissAll();
  }

  getList() {
    const user_id = this.Globals.getUser()._id;
    const query =
      "?operator_id=" +
      user_id +
      "&limit=10&page=" +
      (this.page == 0 ? 0 : this.page - 1);
    this.apiCallingService
      .callAPI({}, "GET", ConfigApi.URLS.EMPLOYEE.LIST + query)
      .subscribe((res) => {
        this.data = res.data;
        this.totalcount = res.total;
      });
  }

  onSubmit(f: any) {
    const rawUsername = f.value.username || "";
    const rawEmail = f.value.email || "";

    if (/\s/.test(rawUsername)) {
      this.toastr.error("Spaces are not allowed in username");
      return;
    }

    if (/\s/.test(rawEmail)) {
      this.toastr.error("Spaces are not allowed in email");
      return;
    }

    const cleanedUsername = rawUsername.toLowerCase();
    const cleanedEmail = rawEmail.toLowerCase();

    // Ensure dashboard permission is included
    this.addDefaultDashboardPermission();

    // Create the payload with the permissions array in the format requested
    const payload = {
      operator_id: this.Globals.getUser()._id,
      name: f.value.name,
      email: cleanedEmail,
      username: cleanedUsername,
      password: f.value.password,
      permissions: this.selectedPermissions,
    };

    console.log("Submitting employee with payload:", payload);

    let url = ConfigApi.URLS.EMPLOYEE.CREATE;
    let method = "POST";
    if (this.modeltitle == "Edit Employee") {
      url = ConfigApi.URLS.EMPLOYEE.UPDATE + "/" + this.detail._id;
      method = "PUT";
    }

    this.apiCallingService.callAPI(payload, method, url).subscribe(
      (res) => {
        if (this.apiCallingService.handleSuccess(res)) {
          this.toastr.success(res.msg);
          this.closeBtnClick();
          this.ngOnInit();
        } else {
          this.toastr.error(res.msg);
        }
      },
      (err) => {
        this.toastr.error(err.error.msg, "Error");
      }
    );
  }

  delete(): void {
    this.apiCallingService
      .callAPI(
        {},
        "DELETE",
        ConfigApi.URLS.EMPLOYEE.DELETE + "/" + this.detail._id
      )
      .subscribe((res) => {
        if (this.apiCallingService.handleSuccess(res)) {
          this.toastr.success(res.msg);
          this.closeBtnClick();
          this.ngOnInit();
        }
      });
  }

  loadPage(page: number) {
    this.page = page;
    this.getList();
  }
}
